import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Share2, Loader2, FileText, User, Globe, Phone, Mail, Calendar, Shield, Image, X } from "lucide-react";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECLARATIONS = [
  "I confirm that the passenger will exit the UAE before visa expiry or complete a valid visa renewal.",
  "I accept full responsibility for any overstay fines, penalties, absconding charges, legal fees, or immigration violations.",
  "In case of visa renewal via airport or bus, I agree to bear the full return ticket cost if the visa is rejected or denied.",
  "I understand that visa approval or rejection is solely at the discretion of UAE Immigration Authorities.",
  "I acknowledge that White Sky Travel and Tourism LLC has no control or influence over visa approvals or rejections.",
  "I confirm that all documents and information provided are genuine, accurate, and valid.",
  "I confirm that the passenger is fully aware of the visa application process, even if applied by a third party.",
  "I declare that there is no other pending or active visa application for the passenger in UAE immigration systems.",
  "I understand that fees paid are subject to immigration rules and may be non-refundable.",
  "I consent to electronic records and digital signatures as legally binding.",
];

const LetterDetail = () => {
  const { letterId } = useParams();
  const navigate = useNavigate();
  const [letter, setLetter] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchLetter();
    fetchDocuments();
  }, [letterId, token]);

  const fetchLetter = async () => {
    try {
      const response = await axios.get(`${API}/guarantee-letter/${letterId}`);
      setLetter(response.data);
    } catch (error) {
      console.error("Error fetching letter:", error);
      toast.error("Failed to load guarantee letter");
      navigate("/admin/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/guarantee-letter/${letterId}/documents`, { headers });
      setDocuments(response.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
      // Don't show error - documents might not exist for older records
    }
  };

  const handleDownloadPDF = () => {
    setDownloading(true);
    try {
      // Open PDF in new tab which will trigger download
      const pdfUrl = `${API}/guarantee-letter/${letterId}/pdf`;
      window.open(pdfUrl, "_blank");
      toast.success("PDF opened in new tab");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(
      `*Guarantee Letter - ${letter?.reference_number}*\n\n` +
      `Passenger: ${letter?.passenger?.full_name}\n` +
      `Passport: ${letter?.passenger?.passport_number}\n` +
      `Visa Type: ${letter?.visa_type}\n` +
      `Date: ${new Date(letter?.created_at).toLocaleDateString()}\n\n` +
      `This is a liability confirmation for UAE visa services from White Sky Travel and Tourism LLC.\n\n` +
      `Contact: +971 4 220 2133 | +971 52 292 1011`
    );
    
    window.open(`https://api.whatsapp.com/send?text=${message}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!letter) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Letter not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShareWhatsApp}
              data-testid="share-whatsapp-btn"
            >
              <Share2 className="w-4 h-4 mr-1" />
              Share
            </Button>
            <Button
              size="sm"
              onClick={handleDownloadPDF}
              disabled={downloading}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="download-pdf-btn"
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              Download PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Reference Header */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">Reference Number</p>
                <p className="text-2xl font-mono font-bold text-slate-900" data-testid="reference-number">
                  {letter.reference_number}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <span className="status-badge active text-sm px-3 py-1">Active</span>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Created</p>
                  <p className="font-medium text-slate-900">
                    {new Date(letter.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Applicant Details - Support multiple passengers */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <User className="w-5 h-5 text-slate-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Applicant Details 
                  {letter.passengers?.length > 1 && (
                    <span className="text-sm font-normal text-slate-500 ml-2">
                      ({letter.passengers.length} applicants)
                    </span>
                  )}
                </h2>
              </div>
              
              {/* Handle both old (single passenger) and new (multiple passengers) format */}
              {(letter.passengers || [letter.passenger]).map((passenger, index) => (
                <div key={index} className={`space-y-4 ${index > 0 ? 'mt-6 pt-6 border-t border-slate-200' : ''}`}>
                  {letter.passengers?.length > 1 && (
                    <p className="text-sm font-semibold text-slate-700 bg-slate-50 px-2 py-1 rounded">
                      Applicant {index + 1}
                    </p>
                  )}
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Full Name</p>
                      <p className="font-medium text-slate-900">{passenger?.full_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Passport Number</p>
                      <p className="font-medium text-slate-900 font-mono">{passenger?.passport_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Nationality</p>
                      <p className="font-medium text-slate-900">{passenger?.nationality}</p>
                    </div>
                  </div>
                  {passenger?.date_of_birth && (
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Date of Birth</p>
                        <p className="font-medium text-slate-900">{passenger.date_of_birth}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Contact</p>
                      <p className="font-medium text-slate-900">{passenger?.contact_number}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium text-slate-900">{passenger?.email}</p>
                    </div>
                  </div>
                  {passenger?.relation_to_guarantor && (
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Relation to Guarantor</p>
                        <p className="font-medium text-slate-900 capitalize">
                          {passenger.relation_to_guarantor === "other" && passenger.other_relation 
                            ? passenger.other_relation 
                            : passenger.relation_to_guarantor}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Guarantor Details */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <Shield className="w-5 h-5 text-slate-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Guarantor Details</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Full Name</p>
                    <p className="font-medium text-slate-900">{letter.guarantor.full_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">
                      {letter.guarantor.id_type === "emirates_id" ? "Emirates ID" : "Passport"} Number
                    </p>
                    <p className="font-medium text-slate-900 font-mono">{letter.guarantor.id_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Nationality</p>
                    <p className="font-medium text-slate-900">{letter.guarantor.nationality}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Contact</p>
                    <p className="font-medium text-slate-900">{letter.guarantor.contact_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium text-slate-900">{letter.guarantor.email}</p>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-100">
                  <p className="text-xs text-slate-500">Guarantor Type</p>
                  <p className="font-medium text-slate-900 capitalize">{letter.guarantor.guarantor_type}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Visa Type */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Visa Type</h2>
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 rounded-sm">
              <span className="font-medium text-slate-900">{letter.visa_type}</span>
            </div>
          </div>

          {/* Declarations */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Declarations Accepted</h2>
            <div className="space-y-3">
              {DECLARATIONS.map((declaration, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-sm ${
                    letter.declarations[index]
                      ? "bg-emerald-50 border border-emerald-200"
                      : "bg-slate-50 border border-slate-200"
                  }`}
                >
                  <span className={letter.declarations[index] ? "text-emerald-600" : "text-slate-400"}>
                    {letter.declarations[index] ? "☑" : "☐"}
                  </span>
                  <span className="text-sm text-slate-700">{declaration}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Uploaded Documents */}
          {documents && (documents.passport_images?.some(img => img) || documents.guarantor_id_image) && (
            <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Image className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">Uploaded Documents</h2>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Passport Images */}
                {documents.passport_images?.map((img, index) => img && (
                  <div key={`passport-${index}`} className="relative group">
                    <p className="text-xs text-slate-500 mb-2">
                      {documents.passport_images.length > 1 ? `Applicant ${index + 1} Passport` : 'Passport'}
                    </p>
                    <div 
                      className="border border-slate-200 rounded-sm overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => setSelectedImage({ src: img, title: `Applicant ${index + 1} Passport` })}
                    >
                      <img 
                        src={img} 
                        alt={`Passport ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-1 text-center">Click to view</p>
                  </div>
                ))}
                
                {/* Guarantor ID Front */}
                {documents.guarantor_id_image && (
                  <div className="relative group">
                    <p className="text-xs text-slate-500 mb-2">Guarantor ID (Front)</p>
                    <div 
                      className="border border-slate-200 rounded-sm overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => setSelectedImage({ src: documents.guarantor_id_image, title: 'Guarantor ID (Front)' })}
                    >
                      <img 
                        src={documents.guarantor_id_image} 
                        alt="Guarantor ID Front"
                        className="w-full h-32 object-cover"
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-1 text-center">Click to view</p>
                  </div>
                )}
                
                {/* Guarantor ID Back */}
                {documents.guarantor_id_back_image && (
                  <div className="relative group">
                    <p className="text-xs text-slate-500 mb-2">Guarantor ID (Back)</p>
                    <div 
                      className="border border-slate-200 rounded-sm overflow-hidden cursor-pointer hover:border-blue-400 transition-colors"
                      onClick={() => setSelectedImage({ src: documents.guarantor_id_back_image, title: 'Guarantor ID (Back)' })}
                    >
                      <img 
                        src={documents.guarantor_id_back_image} 
                        alt="Guarantor ID Back"
                        className="w-full h-32 object-cover"
                      />
                    </div>
                    <p className="text-xs text-blue-600 mt-1 text-center">Click to view</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Signature */}
          <div className="bg-white border border-slate-200 rounded-sm p-6 mt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Digital Signature</h2>
            <div className="flex items-center justify-between">
              <div>
                {letter.signature_data && (
                  <img
                    src={letter.signature_data}
                    alt="Signature"
                    className="h-20 border border-slate-200 rounded-sm bg-white p-2"
                  />
                )}
                <p className="text-sm text-slate-500 mt-2">
                  Signed by: <span className="font-medium text-slate-900">{letter.guarantor.full_name}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Date Signed</p>
                <p className="font-medium text-slate-900">
                  {new Date(letter.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Image Viewer Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-slate-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <p className="text-white text-center mb-4 font-medium">{selectedImage.title}</p>
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-sm"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default LetterDetail;
